import { Link } from "@simplybusiness/mobius";
import type {
  TradeOption,
  TradeSelectorProps,
  TradeSelectorVariant,
  TradeSelectionMeta,
} from "@simplybusiness/mobius-trade-selector";
import { TradeSelector } from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext } from "@simplybusiness/services";
import { useEffect, useState } from "react";
import { useEnvironmentValues } from "../../../../../../../../hooks/environment";
import { useAnswer } from "../../../../../../../../features/questionnaire";
import { useExperiment } from "../../../../../../../../hooks/experiments";
import { QuestionLabel } from "../../../../QuestionLabel";
import type { QuestionComponentProps } from "../../../types";
import { redirect } from "../redirect";
import { TradeQuestionSelected } from "../TradeQuestionSelected";
import { EMPTY_TRADE_OPTION, getTradeOptionFromAnswer } from "../utils";

export const ExperimentTradeQuestion = ({
  questionFieldProps,
  config,
  type,
}: QuestionComponentProps) => {
  const {
    onChange,
    name,
    label,
    errorMessage,
    questionLabelProps,
    helpInfo: _helpInfo,
    ...otherQuestionProps
  } = questionFieldProps;
  const {
    occupationsPageUrl,
    allowTradeBasedRedirect,
    ...tradeSelectorConfig
  } = config || {};
  const answerValue = useAnswer(name);
  const answeredTrade = answerValue?.toString() ?? "";

  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext();

  const [selectedTrade, setSelectedTrade] = useState<TradeOption>(
    getTradeOptionFromAnswer(answeredTrade),
  );

  // Keep selectedTrade in sync with answeredTrade
  useEffect(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(getTradeOptionFromAnswer(answeredTrade));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredTrade]);

  // Determine variant based on uk-26-02-business-trade-selector experiment
  const experimentVariant = useExperiment("uk2602BusinessTradeSelector");
  const getLabel = (experimentVariant: string) => {
    switch (experimentVariant) {
      case "control":
        return "Select a trade";
      case "no-primary-text":
        return "";
      case "updated-text":
        return "Select a trade / profession";
      default:
        return "Select a trade";
    }
  };

  const variant: TradeSelectorVariant =
    type === "trade_search_ai" ? "ai" : "default";
  const handleSelectTrade = (trade: TradeOption, meta?: TradeSelectionMeta) => {
    onChange?.({
      target: {
        name,
        value: trade.value,
        tradeSelectionMeta: meta,
      },
    });
    setSelectedTrade(trade);
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      verticalsWithoutRedirect: trade.verticalsWithoutRedirect,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(allowTradeBasedRedirect),
    });
  };

  const handleTradeChangeClick = () => {
    // Send snowplow event for changing trade
    snowplow?.trigger("tradeChanged", {
      label: questionFieldProps.id,
      property: answeredTrade,
    });
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };

  return (
    <div className="mobius-trade-question">
      {selectedTrade.value ? (
        <TradeQuestionSelected
          trade={selectedTrade}
          onEditTrade={handleTradeChangeClick}
          primaryText={questionLabelProps.primaryText}
        />
      ) : (
        <>
          <QuestionLabel {...questionLabelProps} />
          <TradeSelector
            defaultValue={answeredTrade}
            onSelectTrade={handleSelectTrade}
            variant={variant}
            label={getLabel(experimentVariant ?? "")}
            {...(tradeSelectorConfig as unknown as TradeSelectorProps)}
            site={site}
            vertical={vertical!}
            {...otherQuestionProps}
            {...(experimentVariant === "no-primary-text" &&
              questionLabelProps.labelProps.id && {
                "aria-label": "Select a trade",
              })}
          />
          {typeof occupationsPageUrl === "string" && (
            <div className="mobius-trade-question__note">
              Or choose from the{" "}
              <Link href={occupationsPageUrl}>full list of occupations</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};
